import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Button, FormWrapper, ImageUploadModal, SubmitButton, ToggleButton, ViewMediaModal } from "../../components";

const ProjectNearbyForm = ({ id, onNextStep, isCurrentStep }) => {
  const [showForm, setShowForm] = useState(isCurrentStep);
  const [nearbyStores, setNearbyStores] = useState([]);
  const [newNearbyStore, setNewNearbyStore] = useState({ store_name: "", distance: "", icon: "", icon_url: "" });
  const [showNewInput, setShowNewInput] = useState(nearbyStores.length < 2);
  useEffect(() => setShowForm(isCurrentStep), [isCurrentStep]);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [uploadOptions, setUploadOptions] = useState({
    title: "Upload Store Icon",
    handleClose: () => setShowImageUploadModal(false),
    handleUpload: () => {},
  });
  const [showViewMediaModal, setShowViewMediaModal] = useState(false);
  const [viewMediaOptions, setViewMediaOptions] = useState({});

  // handle form submission
  const handleSubmit = () => {
    const savedStores = JSON.parse(localStorage.getItem(id));
    if (!savedStores || savedStores.length < 2) {
      toast.error("Please add at least 2 nearby stores");
      return;
    }
    onNextStep({ nearby_stores: savedStores });
  };

  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);

  const handleCloseInput = () => {
    setNewNearbyStore({ store_name: "", distance: "", icon: "", icon_url: "" });
    setError("");
    setShowError(false);
    setShowNewInput(false);
  };

  const handleSaveStore = (e) => {
    e.preventDefault();
    if (!newNearbyStore.store_name || newNearbyStore.store_name === "") {
      setError("Please enter a valid store name");
      setShowError(true);
      return;
    }

    if (!newNearbyStore.icon) {
      setError("Please upload a valid icon");
      setShowError(true);
      return;
    }

    if (!newNearbyStore.distance || newNearbyStore.distance === "") {
      setError("Please enter a valid distance");
      setShowError(true);
      return;
    }

    const savedData = JSON.parse(localStorage.getItem(id));
    if (savedData) localStorage.setItem(id, JSON.stringify([...savedData, newNearbyStore]));
    else localStorage.setItem(id, JSON.stringify([newNearbyStore]));

    setNearbyStores((prevNearbyStores) => [...prevNearbyStores, newNearbyStore]);
    handleCloseInput();
  };

  useEffect(() => {
    const savedStores = JSON.parse(localStorage.getItem(id));
    if (savedStores) {
      setNearbyStores(savedStores);
      setShowNewInput(false);
    }
  }, [id]);

  const handleIconUpload = (id, url, key) => {
    if (key === "new") {
      setNewNearbyStore((prev) => ({ ...prev, icon: id, icon_url: url }));
    } else {
      setNearbyStores((prev) =>
        prev.map((nearbyStore, index) => (index === key ? { ...nearbyStore, icon: id, icon_url: url } : nearbyStore))
      );
    }
    setShowImageUploadModal(false);
  };

  const handleNewStoreIconUpload = () => {
    setShowImageUploadModal(true);
    setUploadOptions((prev) => ({ ...prev, handleUpload: (id, url) => handleIconUpload(id, url, "new") }));
  };

  const handleStoreIconUpload = (index) => {
    setShowImageUploadModal(true);
    setUploadOptions((prev) => ({ ...prev, handleUpload: (id, url) => handleIconUpload(id, url, index) }));
  };

  const handleViewMedia = (key) => {
    if (key === "new") setViewMediaOptions({ title: "Store Icon", url: [newNearbyStore.icon_url], mediaType: "image" });
    else {
      const selectedNearbyStore = nearbyStores[key];
      setViewMediaOptions({ title: "Store Icon", url: [selectedNearbyStore.icon_url], mediaType: "image" });
    }
    setShowViewMediaModal(true);
  };

  return (
    <>
      <section className="section_wrapper">
        <div className="section_title_wrapper">
          <h3 className="section_title">What is nearby?</h3>
          <ToggleButton open={showForm} onClick={() => setShowForm((prev) => !prev)} />
        </div>
        <section className={`section_wrapper ${showForm ? "" : "hide"}`}>
          {showNewInput ? (
            <FormWrapper id="nearby_store_form" onSubmit={handleSaveStore}>
              <div className="column">
                <div className="input_wrapper">
                  <div className="row">
                    <input
                      name="store_name"
                      className="input full"
                      type="text"
                      required={showNewInput}
                      placeholder="Enter what is nearby"
                      onChange={(e) =>
                        setNewNearbyStore({
                          ...newNearbyStore,
                          store_name: e.target.value.replace(
                            /\w\S*/g,
                            (txt) => txt.charAt(0).toUpperCase() + txt.slice(1)
                          ),
                        })
                      }
                      value={newNearbyStore.store_name}
                      disabled={!isCurrentStep}
                      maxLength={20}
                      pattern="(^[A-Za-z_@./#&+\-]*$)"
                      title="Only alphabets, special characters are allowed"
                    />
                    <div className="input_with_suffix_wrapper">
                      <input
                        type="number"
                        step="0.1"
                        className="input"
                        required={showNewInput}
                        onChange={(e) =>
                          setNewNearbyStore({
                            ...newNearbyStore,
                            distance: Number(e.target.value),
                          })
                        }
                        value={newNearbyStore.distance}
                        min={0}
                        max={50}
                        disabled={!isCurrentStep}
                      />
                      <span className="suffix">Km</span>
                    </div>
                    {newNearbyStore.icon && newNearbyStore.icon_url ? (
                      <Button className="ghost_gray" onClick={() => handleViewMedia("new")}>
                        preview
                      </Button>
                    ) : (
                      <span className="small gray">No Icon Uploaded</span>
                    )}
                    <Button className="upload" onClick={handleNewStoreIconUpload} disabled={!isCurrentStep}>
                      Upload
                    </Button>
                  </div>
                  {showError && <span className="small error">{error}</span>}
                </div>
                <div className="row end">
                  {nearbyStores.length > 0 && (
                    <Button className="ghost_gray" onClick={handleCloseInput}>
                      Cancel
                    </Button>
                  )}
                  <SubmitButton form="nearby_store_form" className="cta" disabled={!isCurrentStep}>
                    Save
                  </SubmitButton>
                </div>
              </div>
            </FormWrapper>
          ) : (
            <></>
          )}
          {nearbyStores.length > 0 && (
            <div className="column">
              {nearbyStores.map((nearbyStore, index) => (
                <div className="row" key={index}>
                  <span className="nearby_store_label label">{nearbyStore.store_name}</span>
                  <span>{nearbyStore.distance} km</span>
                  {nearbyStore.icon ? (
                    <Button className="ghost_gray" onClick={() => handleViewMedia(index)}>
                      preview
                    </Button>
                  ) : (
                    <span className="small gray">No Icon Uploaded</span>
                  )}
                  <Button className="upload" onClick={() => handleStoreIconUpload(index)}>
                    Upload Icon
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button
            className="ghost_outline"
            onClick={() => setShowNewInput(true)}
            disabled={showNewInput || !isCurrentStep}
            icon="ic:outline-plus"
            iconPosition="start"
            start
          >
            Add Nearby Store
          </Button>
          <Button
            className="submit"
            end
            disabled={nearbyStores.length === 0 || !isCurrentStep || showNewInput}
            onClick={handleSubmit}
          >
            SAVE
          </Button>
        </section>
      </section>

      {showImageUploadModal &&
        createPortal(
          <ImageUploadModal
            isVisible={showImageUploadModal}
            {...uploadOptions}
            handleClose={setShowImageUploadModal}
          />,
          document.body
        )}

      {showViewMediaModal &&
        createPortal(
          <ViewMediaModal
            isVisible={showViewMediaModal}
            {...viewMediaOptions}
            handleClose={() => setShowViewMediaModal(false)}
          />,
          document.body
        )}
    </>
  );
};
export default ProjectNearbyForm;
